import React, { useState, useMemo, useCallback } from "react";
import { useFormikContext } from "formik";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ClearIcon from "@mui/icons-material/Clear";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { FieldArray } from "formik";
import AddressModal from "./AddressModal";
import ConfirmDeleteModal from "../../../components/images/ConfirmDeleteModal";
import CardEditable from "../../card/CardEditable";
import useMutation from "src/hooks/useMutation";
import {
  ADD_PROJECT_ADDRESS_MUTATION,
  EXPORT_PROJECT_DELIVERY_ADDRESSES_EXCEL_MUTATION,
} from "src/constants/graphql/mutations";
import { useParams } from "react-router";
import { useBanner } from "src/contexts/banner";

const ADDRESSES_KEY = "addresses";
const INITIAL_DISPLAY_COUNT = 10;

export default function DeliveryAddresses() {
  const { values, setFieldValue, submitForm } = useFormikContext();
  const { projectId } = useParams();
  const [addProjectAddress] = useMutation(ADD_PROJECT_ADDRESS_MUTATION);
  const [exportProjectDeliveryAddressesExcel] = useMutation(
    EXPORT_PROJECT_DELIVERY_ADDRESSES_EXCEL_MUTATION
  );
  const { displayBanner } = useBanner();
  const [editIndex, setEditIndex] = useState(null);
  const [currentAddress, setCurrentAddress] = useState(null);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [addressToDelete, setAddressToDelete] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showAll, setShowAll] = useState(false);
  const { addresses = [] } = values;

  const handleOpenAddModal = () => {
    setEditIndex(null);
    setCurrentAddress({});
  };

  const handleOpenEditModal = (address, index) => {
    setEditIndex(index);
    setCurrentAddress(address);
  };

  const handleCloseModal = () => {
    setCurrentAddress(null);
  };

  const handleSaveAddress = newAddress => {
    if (editIndex !== null) {
      const updatedAddresses = [...addresses];
      updatedAddresses[editIndex] = newAddress;
      setFieldValue(ADDRESSES_KEY, updatedAddresses);
      return submitForm().then(() => {
        setCurrentAddress(null);
      });
    }

    return addProjectAddress({
      variables: { projectId, ...newAddress },
      onSuccess: data => {
        const updatedAddresses = data?.project?.addresses || [];
        setFieldValue(ADDRESSES_KEY, updatedAddresses);
        setCurrentAddress(null);
      },
    });
  };

  const handleDeleteClick = index => {
    setAddressToDelete(index);
    setOpenDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    const updatedAddresses = [...addresses];
    updatedAddresses.splice(addressToDelete, 1);
    setFieldValue(ADDRESSES_KEY, updatedAddresses);
    setOpenDeleteModal(false);
    return submitForm();
  };

  const handleExport = useCallback(() => {
    if (addresses.length === 0) return;

    displayBanner({
      message: "Generating file...",
      type: "export-excel",
      projectId,
      startedAt: Date.now(),
    });
    exportProjectDeliveryAddressesExcel({
      variables: { projectId },
    });
  }, [addresses.length, displayBanner, exportProjectDeliveryAddressesExcel, projectId]);

  // Filter addresses based on search query
  const filteredAddresses = useMemo(() => {
    if (!searchQuery.trim()) return addresses;

    const query = searchQuery.toLowerCase();
    return addresses.filter((address, index) => {
      const addressName = address.addressName || `Address ${index + 1}`;
      return (
        addressName.toLowerCase().includes(query) ||
        address.street?.toLowerCase().includes(query) ||
        address.city?.toLowerCase().includes(query) ||
        address.state?.toLowerCase().includes(query) ||
        address.zipCode?.toLowerCase().includes(query)
      );
    });
  }, [addresses, searchQuery]);

  // Determine which addresses to display
  const displayedAddresses = useMemo(() => {
    if (showAll || searchQuery.trim()) return filteredAddresses;
    return filteredAddresses.slice(0, INITIAL_DISPLAY_COUNT);
  }, [filteredAddresses, showAll, searchQuery]);

  const hasMore = filteredAddresses.length > INITIAL_DISPLAY_COUNT;
  const hiddenCount = filteredAddresses.length - INITIAL_DISPLAY_COUNT;

  return (
    <Box mt={3}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Box display="flex" alignItems="center" gap={0.5}>
          <Typography fontWeight={400}>
            Delivery Addresses {addresses.length > 0 && `(${addresses.length})`}
          </Typography>
          <Tooltip title={addresses.length > 0 ? "Export addresses to Excel" : "No addresses to export"}>
            <span>
              <IconButton
                size="small"
                onClick={handleExport}
                disabled={addresses.length === 0}
                aria-label="export delivery addresses"
              >
                <FileDownloadOutlinedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {addresses.length > INITIAL_DISPLAY_COUNT && (
        <Box mb={2}>
          <TextField
            fullWidth
            placeholder="Search addresses"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchQuery("")}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {searchQuery && (
            <Typography variant="caption" color="text.secondary" mt={0.5} display="block">
              {filteredAddresses.length} {filteredAddresses.length === 1 ? "address" : "addresses"}{" "}
              found
            </Typography>
          )}
        </Box>
      )}

      <FieldArray
        name={ADDRESSES_KEY}
        render={arrayHelpers => (
          <Grid container spacing={1}>
            {displayedAddresses.map((address, displayIndex) => {
              const originalIndex = addresses.findIndex(a => a === address);
              return (
                <Grid key={originalIndex} size={{ xs: 12, sm: 6 }}>
                  <CardEditable
                    title={address.addressName || `Address ${originalIndex + 1}`}
                    onEdit={() => handleOpenEditModal(address, originalIndex)}
                    onDelete={() => handleDeleteClick(originalIndex)}
                  >
                    <Typography>{address.street}</Typography>
                    <Typography>
                      {[address.city, address.state].filter(Boolean).join(", ") +
                        (address.zipCode ? ` ${address.zipCode}` : "")}
                    </Typography>
                  </CardEditable>
                </Grid>
              );
            })}

            {!showAll && hasMore && !searchQuery && (
              <Grid size={12}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<ExpandMoreIcon />}
                  onClick={() => setShowAll(true)}
                  sx={{ mt: 1 }}
                >
                  Show all {addresses.length} addresses ({hiddenCount} more)
                </Button>
              </Grid>
            )}

            <Grid size={12}>
              <Button variant="outlined" startIcon={<AddIcon />} onClick={handleOpenAddModal}>
                {addresses.length === 0 ? "Add an Address" : "Add Another Address"}
              </Button>
            </Grid>
          </Grid>
        )}
      />

      <AddressModal
        open={!!currentAddress}
        onClose={handleCloseModal}
        address={currentAddress}
        onSave={handleSaveAddress}
        isNew={editIndex === null}
      />

      {openDeleteModal && (
        <ConfirmDeleteModal
          title="Delete Address"
          descr="Are you sure you want to delete the selected address?"
          onClose={() => setOpenDeleteModal(false)}
          onDelete={handleConfirmDelete}
        />
      )}
    </Box>
  );
}
